import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Stack,
  Typography,
  Divider,
  Box,
  useTheme,
  Chip,
} from "@mui/material";
import { Palette, BarChart, Assignment, Dashboard } from "@mui/icons-material";
import GradingIcon from "@mui/icons-material/Grading";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { HandGesture } from "../HandGesture";

export default function CustomizeDashboardModal({
  open,
  onClose,
  customizationSettings = { order: [], visibility: {} },
  handleToggle,
  handleReorder,
}) {
  const theme = useTheme();
  const [showHint, setShowHint] = useState(true);

  // Hide the hint after 5 seconds or when the user drags
  // useEffect(() => {
  //   const timer = setTimeout(() => setShowHint(false), 15000);
  //   return () => clearTimeout(timer);
  // }, []);

  const toggleItems = [
    {
      name: "AccountsCount",
      label: "Accounts Count Section",
      icon: <Assignment color="primary" />,
    },
    {
      name: "AccountsCharts",
      label: "Accounts Charts Section",
      icon: <Assignment color="primary" />,
    },
    {
      name: "showLeads",
      label: "Leads Section",
      icon: <Assignment color="primary" />,
    },
    {
      name: "showTasks",
      label: "Tasks Section",
      icon: <Dashboard color="primary" />,
    },
    {
      name: "showPerformance",
      label: "Agent Performance",
      icon: <GradingIcon color="primary" />,
    },
    {
      name: "showGraphs",
      label: "Analytics Graphs",
      icon: <BarChart color="primary" />,
    },
    {
      name: "showPropertiesByCity",
      label: "Properties by Status",
      icon: <BarChart color="primary" />,
    },
  ];

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(customizationSettings.order || []);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    handleReorder(newOrder);
    setShowHint(false); // hide hint once user interacts
  };
  const getActiveSectionsCount = () => {
    return Object.values(customizationSettings.visibility || {}).filter(Boolean)
      .length;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={() => setShowHint(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      {/* Header Accent */}
      <Box
        sx={{
          height: 4,
        }}
      />

      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: theme.palette.primary.light,
                mr: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Palette sx={{ color: "white", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="700">
                Customize Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag to reorder and toggle visibility of sections
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${getActiveSectionsCount()}/${toggleItems.length} active`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3, position: "relative", minHeight: 300 }}>
        <Box sx={{ position: "relative", height: "100%" }}>
          {/* Draggable Section */}
          <DragDropContext 
          onDragEnd={onDragEnd}
          onDragStart={() => setShowHint(false)}
          >
            <Droppable droppableId="sections">
              {(provided) => (
                <Stack
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  spacing={1.5}
                >
                  {(customizationSettings.order || []).map((key, index) => {
                    const item = toggleItems.find((t) => t.name === key);
                    if (!item) return null;
                    return (
                      <Draggable
                        key={item.name}
                        draggableId={item.name}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            px={1.5}
                            py={1.5}
                            sx={{
                              borderRadius: 2,
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? theme.palette.grey[800]
                                  : theme.palette.grey[50],
                              cursor: "grab",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? theme.palette.grey[700]
                                    : theme.palette.grey[100],
                              },
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              {item.icon}
                              <Typography variant="body1" fontWeight="500">
                                {item.label}
                              </Typography>
                            </Box>
                            <Switch
                              checked={
                                !!customizationSettings.visibility?.[item.name]
                              }
                              onChange={() => handleToggle(item.name)}
                              color="primary"
                            />
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>

          {/* 👇 Animated drag hint */}
          {showHint && <HandGesture showHint={showHint} />}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ py: 2, px: 3, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          Close
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ color: "white" }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
